import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { appNotification } from "@/components/ui/AppNotification";

// Core store types
export interface AppState {
  // UI State
  theme: "light" | "dark";
  sidebarCollapsed: boolean;
  notifications: Notification[];
  // Data State
  services: ServiceStatus[];
  aiData: AIData;
  ragData: RAGData;
  systemData: SystemData;
  // Loading States
  loading: Record<string, boolean>;
  errors: Record<string, string | null>;
  // Real-time State
  websocketConnected: boolean;
  lastUpdate: number;
}

// Action types
export interface AppActions {
  // UI Actions
  toggleTheme: () => void;
  toggleSidebar: () => void;
  addNotification: (notification: Omit<Notification, "id" | "timestamp">) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  // Data Actions
  updateServices: (services: ServiceStatus[]) => void;
  updateAIData: (data: Partial<AIData>) => void;
  updateRAGData: (data: Partial<RAGData>) => void;
  updateSystemData: (data: Partial<SystemData>) => void;
  // Loading Actions
  setLoading: (key: string, loading: boolean) => void;
  setError: (key: string, error: string | null) => void;
  clearErrors: () => void;
  // Real-time Actions
  setWebSocketConnected: (connected: boolean) => void;
  updateLastUpdate: () => void;
  // Utility Actions
  reset: () => void;
}

// Combined store type
export type AppStore = AppState & AppActions;

// Data interfaces
export interface Notification {
  id: string;
  type: "info" | "success" | "warning" | "error";
  title: string;
  message: string;
  timestamp: number;
  read: boolean;
  persistent?: boolean;
  autoDismiss?: number;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
    variant?: "primary" | "secondary" | "danger";
  };
  dismissible?: boolean;
  progress?: number;
  group?: string;
}

export interface ServiceStatus {
  id: string;
  name: string;
  status: "healthy" | "unhealthy" | "unknown";
  uptime: number;
  responseTime: number;
  lastCheck: number;
  metrics: {
    cpu: number;
    memory: number;
    connections: number;
    errorRate: number;
  };
}

export interface AIData {
  conversations: Array<Record<string, unknown>>;
  models: Array<Record<string, unknown>>;
  performance: {
    totalRequests: number;
    successRate: number;
    avgResponseTime: number;
  };
}

export interface RAGData {
  documents: Array<Record<string, unknown>>;
  embeddings: Array<Record<string, unknown>>;
  performance: {
    totalQueries: number;
    avgRelevance: number;
    responseTime: number;
  };
}

export interface SystemData {
  resources: {
    cpu: number;
    memory: number;
    disk: number;
    network: number;
  };
  uptime: number;
  version: string;
}

const initialState: AppState = {
  theme: "dark",
  sidebarCollapsed: false,
  notifications: [],
  services: [],
  aiData: {
    conversations: [],
    models: [],
    performance: {
      totalRequests: 0,
      successRate: 0,
      avgResponseTime: 0,
    },
  },
  ragData: {
    documents: [],
    embeddings: [],
    performance: {
      totalQueries: 0,
      avgRelevance: 0,
      responseTime: 0,
    },
  },
  systemData: {
    resources: {
      cpu: 0,
      memory: 0,
      disk: 0,
      network: 0,
    },
    uptime: 0,
    version: "3.0.0", // Default version from API Gateway
  },
  loading: {},
  errors: {},
  websocketConnected: false,
  lastUpdate: Date.now(),
};

// Create store with middleware
function withStoreMiddleware<T>(initializer: T): T {
  if (import.meta.env.DEV) {
    return devtools(initializer as never, { name: "smart-ops-store" }) as T;
  }
  return initializer;
}

export const useAppStore = create<AppStore>()(
  withStoreMiddleware(
    persist(
      subscribeWithSelector(
        immer((set) => ({
          ...initialState,
          // UI Actions
          toggleTheme: () =>
            set((state) => {
              state.theme = state.theme === "light" ? "dark" : "light";
            }),
          toggleSidebar: () =>
            set((state) => {
              state.sidebarCollapsed = !state.sidebarCollapsed;
            }),
          // Bridge only — UI is AppNotificationHost. Do not grow a second notification panel.
          addNotification: (notification) => {
            appNotification.show(notification.type, notification.title, {
              id: `${notification.type}:${notification.title}:${notification.message ?? ""}`,
              description: notification.message,
              durationMs: notification.persistent
                ? 24 * 60 * 60 * 1000
                : notification.autoDismiss ?? notification.duration,
              dismissible: notification.dismissible,
              action: notification.action
                ? {
                    label: notification.action.label,
                    onClick: notification.action.onClick,
                  }
                : undefined,
            });
          },
          removeNotification: (id) =>
            set((state) => {
              state.notifications = state.notifications.filter((n) => n.id !== id);
            }),
          clearNotifications: () =>
            set((state) => {
              state.notifications = [];
            }),
          // Data Actions
          updateServices: (services) =>
            set((state) => {
              state.services = services;
            }),
          updateAIData: (data) =>
            set((state) => {
              Object.assign(state.aiData, data);
            }),
          updateRAGData: (data) =>
            set((state) => {
              Object.assign(state.ragData, data);
            }),
          updateSystemData: (data) =>
            set((state) => {
              Object.assign(state.systemData, data);
            }),
          // Loading Actions
          setLoading: (key, loading) =>
            set((state) => {
              state.loading[key] = loading;
            }),
          setError: (key, error) =>
            set((state) => {
              state.errors[key] = error;
            }),
          clearErrors: () =>
            set((state) => {
              state.errors = {};
            }),
          // Real-time Actions
          setWebSocketConnected: (connected) =>
            set((state) => {
              state.websocketConnected = connected;
            }),
          updateLastUpdate: () =>
            set((state) => {
              state.lastUpdate = Date.now();
            }),
          // Utility Actions
          reset: () => set(() => ({ ...initialState })),
        }))
      ),
      {
        name: "smart-ops-store",
        partialize: (state) => ({
          theme: state.theme,
          sidebarCollapsed: state.sidebarCollapsed,
        }),
      }
    )
  )
);

// Selectors for performance optimization
export const useTheme = () => useAppStore((state) => state.theme);
export const useSidebarCollapsed = () => useAppStore((state) => state.sidebarCollapsed);
export const useNotifications = () => useAppStore((state) => state.notifications);
export const useServices = () => useAppStore((state) => state.services);
export const useAIData = () => useAppStore((state) => state.aiData);
export const useRAGData = () => useAppStore((state) => state.ragData);
export const useSystemData = () => useAppStore((state) => state.systemData);
export const useLoading = (key: string) => useAppStore((state) => state.loading[key] || false);
export const useError = (key: string) => useAppStore((state) => state.errors[key]);
export const useWebSocketConnected = () => useAppStore((state) => state.websocketConnected);

// Actions
export const useAppActions = () =>
  useAppStore((state) => ({
    toggleTheme: state.toggleTheme,
    toggleSidebar: state.toggleSidebar,
    addNotification: state.addNotification,
    removeNotification: state.removeNotification,
    clearNotifications: state.clearNotifications,
    updateServices: state.updateServices,
    updateAIData: state.updateAIData,
    updateRAGData: state.updateRAGData,
    updateSystemData: state.updateSystemData,
    setLoading: state.setLoading,
    setError: state.setError,
    clearErrors: state.clearErrors,
    setWebSocketConnected: state.setWebSocketConnected,
    updateLastUpdate: state.updateLastUpdate,
    reset: state.reset,
  }));

export default useAppStore;
